// src/components/Colaborador/AreaENPS.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import Chart from 'react-apexcharts';

const AreaENPS = ({ departamentoId, empresaId }) => {
  const [enpsData, setEnpsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!departamentoId || !empresaId) {
      setError("ID do departamento ou ID da empresa ausente.");
      setLoading(false);
      return;
    }
    const fetchENPS = async () => {
      setLoading(true); // Start loading
      setError(null); // Reset error before new fetch
      // setEnpsData(null); // Optionally reset enpsData before fetch if you want to ensure loading state shows
      const deptoDocRef = doc(db, 'Empresas', empresaId, 'Departamentos', departamentoId);
      try {
        const deptoDocSnap = await getDoc(deptoDocRef);
        if (deptoDocSnap.exists()) {
          const deptoData = deptoDocSnap.data();
          if (deptoData.eNPS && typeof deptoData.eNPS === 'object' && deptoData.eNPS.hasOwnProperty('score')) {
            setEnpsData(deptoData.eNPS);
          } else {
            setEnpsData(null); // No valid eNPS object found, treat as N/A or data not available
            // setError("Dados de e-NPS não encontrados ou em formato inválido para o departamento."); // Optional: more specific message
          }
        } else {
          setEnpsData(null);
          setError(`Departamento não encontrado.`);
        }
      } catch (err) {
        console.error("[AreaENPS] Erro ao buscar dados do Termômetro da Felicidade:", err);
        if (err.code === 'permission-denied') {
          setError(`Permissão negada para acessar dados do Termômetro da Felicidade.`);
        } else {
          setError(`Falha ao carregar Termômetro da Felicidade: ${err.message}`);
        }
        setEnpsData(null); // Ensure no stale data is shown on error
      } finally {
        setLoading(false); // Stop loading regardless of outcome
      }
    };
    fetchENPS();
  }, [departamentoId, empresaId]);

  const { series, options, chartHeight } = useMemo(() => {
    const score = enpsData?.score ?? null;
    // Define chart appearance based on state
    const newChartHeight = 420; // CHART HEIGHT SET TO 420
    let chartOffsetY = -15;     // Adjusted Y offset for the larger chart

    let displayPercentage = 0;
    let primaryColor = 'var(--medium-gray-color, #B0BEC5)';
    let trackBgColor = 'var(--light-gray-color, #ECEFF1)';
    let valueLabel = "N/D"; // Default to N/D if no score
    let nameLabel = "Score";
    let valueFontSize = '30px'; // Default N/D font size
    let nameFontSize = '18px';  // Default name font size
    let nameOffsetY = -22;
    let valueOffsetY = 18;


    if (loading && !enpsData && !error) { // Only show "Carregando..." if truly loading initial data
      nameLabel = "Carregando...";
      valueLabel = ""; 
      valueFontSize = '20px';
      nameFontSize = '16px';
      primaryColor = 'var(--secondary-color, #6c757d)';
      trackBgColor = 'var(--light-gray-color, #f1f1f1)';
    } else if (error && !enpsData) { // If there's an error and no data at all
      nameLabel = "Erro";
      valueLabel = "-";
      primaryColor = 'var(--danger-color, #dc3545)';
      trackBgColor = 'rgba(220, 53, 69, 0.1)';
      valueFontSize = '36px';
      nameFontSize = '18px';
    } else if (score !== null) {
      displayPercentage = ((score + 100) / 200) * 100;
      valueLabel = String(score);
      valueFontSize = '44px'; // Larger font for the score
      nameFontSize = '20px';  // Larger font for the label "e-NPS Score"
      chartOffsetY = -20;     // Further adjust Y offset for prominent score
      nameOffsetY = -25;
      valueOffsetY = 20;


      if (score >= 50) {
        primaryColor = 'var(--success-color, #198754)';
        trackBgColor = 'rgba(25, 135, 84, 0.15)';
      } else if (score >= 0) {
        primaryColor = 'var(--warning-color, #ffc107)';
        trackBgColor = 'rgba(255, 193, 7, 0.15)';
      } else {
        primaryColor = 'var(--danger-color, #dc3545)';
        trackBgColor = 'rgba(220, 53, 69, 0.15)';
      }
    }
    // Implicitly, if loading is false, no error, but score is still null (e.g. eNPS field missing),
    // it will use the default "N/D" settings.

    return {
      series: [displayPercentage],
      options: {
        chart: {
          type: 'radialBar',
          offsetY: chartOffsetY,
          sparkline: { enabled: true },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 600,
            animateGradually: { enabled: true, delay: 150 },
            dynamicAnimation: { enabled: true, speed: 350 }
          }
        },
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 135,
            hollow: {
              margin: 0,
              size: '68%', // Adjusted hollow size for a thicker bar appearance
              background: 'transparent',
            },
            track: {
              background: trackBgColor,
              strokeWidth: '100%',
              margin: 0,
            },
            dataLabels: {
              show: true,
              name: {
                show: true,
                offsetY: nameOffsetY, 
                fontSize: nameFontSize,
                fontWeight: 500, // Slightly bolder name
                color: 'var(--text-color-muted, #6c757d)',
                formatter: () => nameLabel,
              },
              value: {
                formatter: () => valueLabel,
                offsetY: valueOffsetY, 
                color: primaryColor,
                fontSize: valueFontSize,
                fontWeight: 700,
                show: true,
              },
            },
          },
        },
        colors: [primaryColor],
        stroke: { lineCap: 'round' },
        fill: {
            type: "gradient",
            gradient: {
                shade: "dark",
                type: "horizontal",
                shadeIntensity: 0.5, // Slightly increased intensity
                gradientToColors: [primaryColor], 
                inverseColors: false, // Kept false for a more direct gradient
                opacityFrom: 0.85,
                opacityTo: 1,
                stops: [0, 90, 100] // Adjusted stops for gradient feel
            }
        }
      },
      chartHeight: newChartHeight,
    };
  }, [enpsData, loading, error]);

  const dataPesquisaFormatada = enpsData?.dataPesquisa?.seconds
    ? new Date(enpsData.dataPesquisa.seconds * 1000).toLocaleDateString('pt-BR')
    : (loading && !error) ? 'Carregando...' : 'N/A';

  const numeroRespondentes = enpsData?.numeroRespondentes
    ? enpsData.numeroRespondentes
    : (loading && !error) ? '...' : 'N/A';

  // If still loading initial data (and no error occurred yet), show a specific loading state for the whole widget.
  // This is distinct from the chart's internal "Carregando..." label.
  if (loading && !error && !enpsData) {
    return (
      <>
        <div className="apex-chart-container" style={{ minHeight: `${chartHeight}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* You could put a spinner here or use the chart's loading state */}
          <Chart options={options} series={series} type="radialBar" height={chartHeight} />
        </div>
        <div className="widget-details mt-2">
          <span title="Data da Pesquisa"><i className="fas fa-calendar-alt me-1"></i> Carregando...</span>
          <span title="Número de Respondentes"><i className="fas fa-users me-1"></i> ...</span>
        </div>
      </>
    );
  }
  
  return (
    <> 
      {error && ( // Show error text if an error occurred
        <p className="error-message small enps-error mb-2">
          {error}
        </p>
      )}
      <div className="apex-chart-container" style={{ minHeight: `${chartHeight - 40}px` }}>
        <Chart
          options={options}
          series={series}
          type="radialBar"
          height={chartHeight}
        />
      </div>
      <div className="widget-details"> 
        <span title={`Data da Pesquisa: ${dataPesquisaFormatada}`}>
          <i className="fas fa-calendar-alt"></i> {dataPesquisaFormatada}
        </span>
        <span title={`Número de Respondentes: ${numeroRespondentes}`}>
          <i className="fas fa-users"></i> {numeroRespondentes}
        </span>
      </div>
    </> 
  );
};

export default AreaENPS;